import React from 'react';
import {
	Box,
	Flex,
	Text,
	IconButton,
	Badge,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverHeader,
	PopoverBody,
	PopoverFooter,
	PopoverArrow,
	PopoverCloseButton,
	VStack,
	HStack,
	Button,
	Icon,
	Divider,
	useDisclosure,
} from '@chakra-ui/react';
import {
	FiBell,
	FiAlertCircle,
	FiAlertTriangle,
	FiCheckCircle,
	FiRefreshCw,
	FiArrowRight,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useHeaderNotifications } from '../../../hooks/useHeaderNotifications';
import { translateEventName } from '../../patients/subcomponents/dashboardCard/DashboardCardEventsList';

/**
 * Componente: HeaderNotificationBell
 * Campana de alertas en tiempo real en el Header global.
 * Se actualiza instantáneamente con WebSockets (UnreadEventsContext).
 * Muestra badge con conteo y desplegable de triaje rápido hacia el expediente o bandeja.
 */
export const HeaderNotificationBell = () => {
	const { t } = useTranslation();
	const { onOpen, onClose, isOpen } = useDisclosure();
	const {
		unreadCount,
		hasUnread,
		hasCritical,
		recentEvents,
		refreshEvents,
		navigateToEvents,
		navigateToPatient,
	} = useHeaderNotifications();

	const badgeDisplay = unreadCount > 99 ? '99+' : unreadCount;

	return (
		<Popover
			isOpen={isOpen}
			onOpen={onOpen}
			onClose={onClose}
			placement='bottom-end'
			closeOnBlur={true}
		>
			<PopoverTrigger>
				<Box position='relative' display='inline-block'>
					<IconButton
						type='button'
						icon={<FiBell fontSize='20px' />}
						aria-label={
							hasUnread
								? t('Notifications: {{count}} unread', { count: unreadCount })
								: t('No unread notifications')
						}
						size={{ base: 'sm', md: 'md' }}
						minW='44px'
						minH='44px'
						bg='white'
						color='gray.700'
						border='1px'
						borderColor='gray.200'
						_hover={{ bg: 'gray.50', color: 'teal.600' }}
						_active={{ bg: 'gray.100' }}
						_focusVisible={{
							outline: '3px solid',
							outlineColor: 'teal.500',
							outlineOffset: '2px',
						}}
						borderRadius='md'
						boxShadow='xs'
					/>

					{/* Badge flotante de conteo */}
					{hasUnread && (
						<Box
							position='absolute'
							top='-6px'
							right='-6px'
							display='inline-flex'
							alignItems='center'
							justifyContent='center'
							bg='#E53E3E'
							backgroundColor='#E53E3E'
							color='#FFFFFF'
							borderRadius='full'
							minW='20px'
							h='20px'
							px='6px'
							boxShadow='0 2px 5px rgba(0, 0, 0, 0.35)'
							border='2px solid #FFFFFF'
							pointerEvents='none'
							zIndex={2}
							animation={hasCritical ? 'pulse 2s infinite' : undefined}
						>
							<Text
								as='span'
								color='#FFFFFF'
								fontSize='11px'
								fontWeight='extrabold'
								lineHeight='1'
							>
								{badgeDisplay}
							</Text>
						</Box>
					)}
				</Box>
			</PopoverTrigger>

			<PopoverContent
				w={{ base: '320px', sm: '380px' }}
				bg='white'
				borderColor='gray.200'
				boxShadow='xl'
				borderRadius='xl'
				_focus={{ boxShadow: 'xl' }}
				zIndex={1400}
			>
				<PopoverArrow />
				<PopoverCloseButton size='sm' top={3} right={3} />

				{/* 1. Encabezado del Popover */}
				<PopoverHeader borderBottom='1px' borderColor='gray.100' py={3} px={4}>
					<Flex justify='space-between' align='center' pr={6}>
						<HStack spacing={2}>
							<Icon as={FiBell} color='teal.600' />
							<Text fontWeight='bold' fontSize='sm' color='gray.800'>
								{t('Notifications')}
							</Text>
							{hasUnread && (
								<Badge
									variant='solid'
									bg='#E53E3E'
									color='#FFFFFF'
									borderRadius='full'
									px={2.5}
									py={0.5}
									fontSize='xs'
									fontWeight='bold'
								>
									{unreadCount}
								</Badge>
							)}
						</HStack>

						<IconButton
							type='button'
							icon={<FiRefreshCw />}
							size='xs'
							variant='ghost'
							aria-label={t('Refresh alerts')}
							onClick={refreshEvents}
						/>
					</Flex>
				</PopoverHeader>

				{/* 2. Cuerpo con lista de alertas */}
				<PopoverBody p={0} maxH='360px' overflowY='auto'>
					{!hasUnread || recentEvents.length === 0 ? (
						<Flex direction='column' align='center' justify='center' py={8} px={4} textAlign='center'>
							<Icon as={FiCheckCircle} boxSize='36px' color='green.400' mb={2} />
							<Text fontWeight='semibold' fontSize='sm' color='gray.700'>
								{t('All caught up')}
							</Text>
							<Text fontSize='xs' color='gray.500' mt={1}>
								{t('No pending emergency alerts or critical events.')}
							</Text>
						</Flex>
					) : (
						<VStack spacing={0} align='stretch' divider={<Divider borderColor='gray.100' />}>
							{recentEvents.map(ev => {
								return (
									<Box
										key={ev.id}
										as='button'
										type='button'
										textAlign='left'
										w='100%'
										p={3}
										_hover={{ bg: 'gray.50' }}
										_focusVisible={{ bg: 'gray.50', outline: 'none' }}
										transition='background 0.15s'
										onClick={() => navigateToPatient(ev.patientId, onClose)}
									>
										<Flex justify='space-between' align='flex-start' mb={1}>
											<HStack spacing={1.5}>
												<Icon
													as={ev.isCritical ? FiAlertCircle : FiAlertTriangle}
													color={ev.isCritical ? 'red.500' : 'orange.400'}
													boxSize='14px'
												/>
												<Text
													fontSize='xs'
													fontWeight='bold'
													color={ev.isCritical ? 'red.600' : 'orange.700'}
													textTransform='uppercase'
												>
													{translateEventName(ev.type, t)}
												</Text>
											</HStack>
											{ev.timeAgo && (
												<Text fontSize='10px' color='gray.400' flexShrink={0}>
													{ev.timeAgo}
												</Text>
											)}
										</Flex>

										{ev.patientName && (
											<Text fontSize='xs' fontWeight='semibold' color='gray.800' noOfLines={1}>
												{ev.patientName}
											</Text>
										)}

										<Text fontSize='11px' color='gray.500' mt={0.5} noOfLines={1}>
											{t('Click to open clinical dossier')}
										</Text>
									</Box>
								);
							})}
						</VStack>
					)}
				</PopoverBody>

				{/* 3. Pie con acceso a Bandeja Completa */}
				<PopoverFooter borderTop='1px' borderColor='gray.100' p={3} bg='gray.50' borderBottomRadius='xl'>
					<Button
						type='button'
						rightIcon={<FiArrowRight />}
						size='sm'
						w='100%'
						colorScheme='teal'
						variant='outline'
						onClick={() => navigateToEvents(onClose)}
					>
						{t('View All Events')}
					</Button>
				</PopoverFooter>
			</PopoverContent>
		</Popover>
	);
};

export default HeaderNotificationBell;
